import { useCallback, useState } from 'react';
import type {
  CreateCustomerVendorSubscriptionRequest,
  InvoiceScheduleCreateRequest,
  IncrementScheduleLineRequest,
} from '@techgitter/contracts';

import { createVendorSubscription } from '../../services/vendorSubscriptionService';
import {
  addScheduleLine,
  incrementInvoiceScheduleLine,
  createInvoiceScheduleAndWait,
  type AddScheduleLineRequest,
} from '../../services/invoiceScheduleAdminService';

/**
 * 'submitting' — creating the subscription / billing in progress.
 * 'succeeded'  — subscription created AND the chosen billing action ran (or was
 *                intentionally skipped for a commitment-only resolution).
 * 'partial'    — subscription created, but the billing step failed (best-effort;
 *                the commitment exists, so the coverage gap is resolved — the
 *                operator just needs to fix the billing line manually).
 * 'error'      — subscription creation itself failed (nothing was created).
 */
export type CreateSubAndBillPhase = 'idle' | 'submitting' | 'succeeded' | 'partial' | 'error';

/**
 * How to bill the new commitment. Coverage-state-aware (Fix 2):
 *  - none           — the schedule already covers it; create the commitment only.
 *  - increment      — a line exists but under-bills; bump it (never a duplicate).
 *  - add            — no line yet; append one.
 *  - createSchedule — the customer has no matching schedule; create it + the line.
 */
export type BillingPlan =
  | { mode: 'none' }
  | { mode: 'add'; scheduleId: string; line: AddScheduleLineRequest }
  | { mode: 'increment'; scheduleId: string; increment: IncrementScheduleLineRequest }
  | { mode: 'createSchedule'; createSchedule: InvoiceScheduleCreateRequest };

export interface CreateSubAndBillParams {
  subscription: CreateCustomerVendorSubscriptionRequest;
  billing: BillingPlan;
}

export interface CreateSubAndBillResult {
  subscriptionId: string;
  billing: 'added' | 'incremented' | 'scheduleCreated' | 'skipped' | 'failed';
  billingError?: string;
}

/**
 * Reusable orchestration for "create the commitment (subscription) and reconcile
 * its billing on the customer's recurring schedule". Decoupled from
 * reconciliation — callers pass a coverage-state-aware {@link BillingPlan} and
 * act on the result.
 *
 * Best-effort billing: once the subscription exists we never roll it back; a
 * failed billing step surfaces as 'partial' so the caller can still record the
 * resolution and warn the operator to fix the line manually.
 */
export function useCreateSubscriptionAndBill() {
  const [phase, setPhase] = useState<CreateSubAndBillPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase('idle');
    setError(null);
  }, []);

  const run = useCallback(
    async (params: CreateSubAndBillParams): Promise<CreateSubAndBillResult | null> => {
      setPhase('submitting');
      setError(null);

      let subscriptionId: string;
      try {
        const sub = await createVendorSubscription(params.subscription);
        subscriptionId = sub.id;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to create subscription');
        setPhase('error');
        return null;
      }

      // Subscription (commitment) now exists — reconcile billing best-effort.
      const b = params.billing;
      try {
        if (b.mode === 'none') {
          setPhase('succeeded');
          return { subscriptionId, billing: 'skipped' };
        }
        if (b.mode === 'add') {
          await addScheduleLine(b.scheduleId, b.line);
          setPhase('succeeded');
          return { subscriptionId, billing: 'added' };
        }
        if (b.mode === 'increment') {
          await incrementInvoiceScheduleLine(b.scheduleId, b.increment);
          setPhase('succeeded');
          return { subscriptionId, billing: 'incremented' };
        }
        // createSchedule: RS schedule + DV mirror + the (recurring) line bundled
        // in lineItems. Wait to completion so we only report success once the
        // schedule + line actually exist.
        await createInvoiceScheduleAndWait(b.createSchedule);
        setPhase('succeeded');
        return { subscriptionId, billing: 'scheduleCreated' };
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to reconcile the billing line';
        setError(msg);
        setPhase('partial');
        return { subscriptionId, billing: 'failed', billingError: msg };
      }
    },
    [],
  );

  return { phase, error, run, reset };
}
